import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { type MFAIdentityType } from '@/entities/mfa-identity';
import { useSessionStore } from '@/entities/session';
import { useVerifyTwoFactorIdentityCode } from '@/features/user-settings-phone/api';

type PhoneVerificationTwoFactorIdentityArgs = {
  identityType: MFAIdentityType;
  identity?: string;
  onSuccess?: () => void;
};

const usePhoneVerificationTwoFactorIdentityForm = ({
  identityType,
  identity,
  onSuccess,
}: PhoneVerificationTwoFactorIdentityArgs) => {
  const { session, setSession } = useSessionStore();
  const { t } = useTranslation();

  const required = t('authPages.validation.requiredField');
  const otpIncorrect = t('authPages.validation.otpIncorrect');

  const otpCodeSchema = z
    .string()
    .regex(/^\d+$/, { message: otpIncorrect })
    .nonempty({ message: required });

  const schema = z.object({
    key: z.string().optional(),
    identity: z.string().optional(),
    code: otpCodeSchema,
  });

  const form = useForm({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      code: '',
    },
  });

  const {
    verifyPhoneTwoFactor,
    isVerifyPhoneTwoFactorLoading,
    verifyPhoneTwoFactorError,
  } = useVerifyTwoFactorIdentityCode({
    onSuccess: async () => {
      if (session?.additional) {
        setSession({
          ...session,
          additional: {
            ...session?.additional,
            isPhone: true,
          },
        });
      }

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: error => {
      form.setValue('code', '');
      throw error;
    },
  });

  const submitForm = async () => {
    await verifyPhoneTwoFactor({
      identityType,
      identity: identity,
      code: form.getValues('code'),
    });
  };

  return {
    form: form,
    verifyPhoneTwoFactorSubmit: submitForm,
    isVerifyPhoneTwoFactorLoading,
    verifyPhoneTwoFactorError,
  };
};

export { usePhoneVerificationTwoFactorIdentityForm };
